import { Injectable } from '@angular/core';
import { Tour, TourFrequency } from '@pncl/common-models';
import { LocalStorageItemIDs, LocalStorageService } from 'src/app/services/local-storage.service';

interface TourStorageState {
  dismissedAtByTourId: Record<string, number>;
}

@Injectable({
  providedIn: 'root',
})
export class TourStorageService {
  private readonly storageStateByKey = new Map<LocalStorageItemIDs, TourStorageState>();

  constructor(private readonly localStorageService: LocalStorageService) {}

  // "Force" tours ignore persisted dismissal state so they can always be shown again.
  shouldTourBeSkippedIfAlreadyCompleted(storageKey: LocalStorageItemIDs, tour: Tour): boolean {
    if (tour.frequency === TourFrequency.FORCE) {
      return false;
    }

    const storageState = this.getStorageState(storageKey);
    return storageState.dismissedAtByTourId[tour._id] !== undefined;
  }

  // Persist the dismissal timestamp so non-force tours are suppressed on future visits.
  persistTourDismissal(storageKey: LocalStorageItemIDs, tour: Tour): void {
    const storageState = this.getStorageState(storageKey);
    storageState.dismissedAtByTourId[tour._id] = Date.now();

    this.saveTourStorageState(storageKey, storageState);
  }

  private getStorageState(storageKey: LocalStorageItemIDs): TourStorageState {
    const cachedState = this.storageStateByKey.get(storageKey);
    if (cachedState) {
      return cachedState;
    }

    const storageState = this.loadTourStorageState(storageKey);
    this.storageStateByKey.set(storageKey, storageState);

    return storageState;
  }

  // Reads always return the current dismissal-map shape, even when nothing has been stored yet.
  private loadTourStorageState(storageKey: LocalStorageItemIDs): TourStorageState {
    const savedValue = this.localStorageService.getItem(storageKey);
    if (!savedValue) {
      return createEmptyTourStorageState();
    }

    return JSON.parse(savedValue) as TourStorageState;
  }

  // Centralize writes so every caller persists the same storage format.
  private saveTourStorageState(storageKey: LocalStorageItemIDs, state: TourStorageState): void {
    this.localStorageService.setItem(storageKey, JSON.stringify(state));
    this.storageStateByKey.set(storageKey, state);
  }
}

// Keep the default storage payload in one place for both read and write flows.
function createEmptyTourStorageState(): TourStorageState {
  return {
    dismissedAtByTourId: {},
  };
}
